import { FormGroup } from '@angular/forms';
import { formatDate } from 'src/app/sdc/utils/date.handler';
import { MemberUpdateDetailsModel } from '../models/memberupdatedetails.model';
import { UserDetailsModel } from '../models/userdetails.model';
import { MemberTypeFormDataModel } from '../models/memberdetails.model';

/**
 * Constructs a request for updating member details.
 *
 * @param {any} memberDetails - An object containing the basic details of the member such as name, date of birth, and gender.
 * @param {boolean} termsCheck - A boolean value indicating whether the member has opted in for team offers.
 * @param {any} userInputs - An object containing user inputs, potentially for additional details or corrections.
 * @param {UserDetailsModel} userDetails - An instance of UserDetailsModel containing details about the user.
 * @param {FormGroup} memberAddressForm - A FormGroup instance representing the member's address form.
 * @param {FormGroup} memberPreferenceForm - A FormGroup instance representing the member's communication preferences form.
 * @returns {MemberUpdateDetailsModel} - An instance of MemberUpdateDetailsModel populated with the updated member details.
 *
 * The function takes in various inputs including member basic details, user inputs, user details, and form groups for address and communication preferences. It uses these inputs to create and return an updated instance of MemberUpdateDetailsModel. The function handles different scenarios based on whether the user is a success factor user and whether additional user inputs are provided.
 */
export function constructRequest(
  memberDetails: any,
  termsCheck: boolean,
  userInputs: MemberTypeFormDataModel | undefined,
  userDetails: UserDetailsModel,
  memberAddressForm: FormGroup,
  memberPreferenceForm: FormGroup
): MemberUpdateDetailsModel {
  const memberUpdateDetails = new MemberUpdateDetailsModel();
  memberUpdateDetails.firstName = memberDetails.firstName;
  memberUpdateDetails.lastName = memberDetails.lastName;
  memberUpdateDetails.dob = memberDetails.dob;
  memberUpdateDetails.gender = memberDetails.gender;
  memberUpdateDetails.isTeamOfferOptIn = termsCheck;

  if (userInputs != null) {
    memberUpdateDetails.edrFirstName = userInputs.firstName;
    memberUpdateDetails.edrLastName = userInputs.lastName;
    memberUpdateDetails.edrCardNo = userInputs.cardNo;
    memberUpdateDetails.edrDob = userInputs.dob ? formatDate(new Date(userInputs.dob)) : '';
  }
  if (!userDetails.isSuccessFactorUser) {
    memberUpdateDetails.mailingUnitNumber = memberAddressForm.get('unitNo')?.value;
    memberUpdateDetails.mailingStreetNumber = memberAddressForm.get('streetNo')?.value;
    memberUpdateDetails.mailingStreetName = memberAddressForm.get('streetName')?.value;
    memberUpdateDetails.mailingSuburb = memberAddressForm.get('suburb')?.value;
    memberUpdateDetails.mailingPostcode = memberAddressForm.get('postcode')?.value;
    memberUpdateDetails.mailingState = memberAddressForm.get('state')?.value;
  } else {
    memberUpdateDetails.mailingUnitNumber = memberDetails.mailingUnitNumber;
    memberUpdateDetails.mailingStreetNumber = memberDetails.mailingStreetNumber;
    memberUpdateDetails.mailingStreetName = memberDetails.mailingStreetName;
    memberUpdateDetails.mailingSuburb = memberDetails.mailingSuburb;
    memberUpdateDetails.mailingPostcode = memberDetails.mailingPostcode;
    memberUpdateDetails.mailingState = memberDetails.mailingState;
  }
  memberUpdateDetails.preferredCommsEmail = memberPreferenceForm.get('email')?.value;
  memberUpdateDetails.preferredCommsPhoneNumber = memberPreferenceForm.get('mobile')?.value;
  return memberUpdateDetails;
}
